/**
 * Shared low-level habit state reduction over the event log — mirrors
 * `src/domain/objectives/state.ts`'s shape exactly (same "build a state map,
 * then resolve derived fields in a second pass" structure).
 *
 * Habit kind (build/avoid) is fixed for the life of the habit (ADR-0002:
 * "changing kind = archive + create") — `HabitPlan`'s kind-matched union
 * makes an illegal kind/schedule mismatch unrepresentable at the type level,
 * so this module never needs to guard against it.
 */

import type {
  CompassEvent,
  HabitArchivedEvent,
  HabitCategory,
  HabitId,
  HabitPausedEvent,
  HabitPlan,
  HabitResumedEvent,
  ObjectiveId,
  TargetAdjustment,
} from '../../contracts';
import { eventsOfTypes, revertedEventIds } from '../event-log/event-log';

export interface HabitState {
  readonly habitId: HabitId;
  name: string;
  category: HabitCategory;
  plan: HabitPlan;
  objectiveId: ObjectiveId | null;
  lifecycle: 'active' | 'paused' | 'archived';
  /** occurredOn of habit.created — the habit's counting window start. */
  readonly createdOn: string;
}

const HABIT_LIFECYCLE_EVENT_TYPES = [
  'habit.created',
  'habit.edited',
  'habit.target_adjusted',
  'habit.paused',
  'habit.resumed',
  'habit.archived',
  'log_entry.reverted',
] as const;

/**
 * Build the full per-habit state map from the raw log. Lifecycle events
 * (`created`/`edited`/`paused`/`resumed`/`archived`) are never valid revert
 * targets (mirrors objectives: only log-entry-shaped events are revertible,
 * ADR-0002) — `log_entry.reverted` is consulted only to resolve the
 * effective schedule (a reverted `habit.target_adjusted` must not apply,
 * mirroring `resolveEffectiveTargetDate` in objectives/state.ts).
 */
export function buildHabitStates(
  events: readonly CompassEvent[],
): ReadonlyMap<HabitId, HabitState> {
  const relevant = eventsOfTypes(events, HABIT_LIFECYCLE_EVENT_TYPES);
  const states = new Map<HabitId, HabitState>();

  for (const event of relevant) {
    switch (event.type) {
      case 'habit.created': {
        states.set(event.habitId, {
          habitId: event.habitId,
          name: event.name,
          category: event.category,
          plan: event.plan,
          objectiveId: event.objectiveId,
          lifecycle: 'active',
          createdOn: event.occurredOn,
        });
        break;
      }
      case 'habit.edited': {
        const state = states.get(event.habitId);
        if (!state) break;
        if (event.changes.name !== undefined) state.name = event.changes.name;
        if (event.changes.category !== undefined) state.category = event.changes.category;
        if (event.changes.objectiveId !== undefined) {
          state.objectiveId = event.changes.objectiveId;
        }
        break;
      }
      case 'habit.target_adjusted': {
        // Applied unconditionally here (raw log order); a reverted
        // adjustment is corrected afterwards by `resolveEffectivePlan` below,
        // mirroring objectives/state.ts's two-pass target-date resolution.
        const state = states.get(event.habitId);
        if (!state) break;
        state.plan = { habitKind: event.adjustment.habitKind, schedule: event.adjustment.next } as HabitPlan;
        break;
      }
      case 'habit.paused': {
        applyPause(states, event);
        break;
      }
      case 'habit.resumed': {
        applyResume(states, event);
        break;
      }
      case 'habit.archived': {
        applyArchive(states, event);
        break;
      }
      // log_entry.reverted is consulted by resolveEffectivePlan below.
      default:
        break;
    }
  }

  for (const state of states.values()) {
    state.plan = resolveEffectivePlan(relevant, state.habitId, state.plan);
  }

  return states;
}

function applyPause(
  states: Map<HabitId, HabitState>,
  event: HabitPausedEvent,
): void {
  const state = states.get(event.habitId);
  if (!state) return;
  if (state.lifecycle === 'archived') return;
  state.lifecycle = 'paused';
}

function applyResume(
  states: Map<HabitId, HabitState>,
  event: HabitResumedEvent,
): void {
  const state = states.get(event.habitId);
  if (!state) return;
  if (state.lifecycle === 'archived') return;
  state.lifecycle = 'active';
}

function applyArchive(
  states: Map<HabitId, HabitState>,
  event: HabitArchivedEvent,
): void {
  const state = states.get(event.habitId);
  if (!state) return;
  state.lifecycle = 'archived';
}

/**
 * The effective plan for one habit: the schedule of the most recent
 * (canonical-order) surviving `habit.target_adjusted` event, or the plan
 * from `habit.created` if every adjustment was reverted (or none exist).
 * Mirrors `resolveEffectiveTargetDate` (objectives/state.ts) adapted for a
 * kind-matched plan rather than a single date field.
 */
function resolveEffectivePlan(
  events: readonly CompassEvent[],
  habitId: HabitId,
  fallback: HabitPlan,
): HabitPlan {
  const reverted = revertedEventIds(events);

  let originalPlan: HabitPlan | null = null;
  let effectivePlan: HabitPlan | null = null;

  for (const event of events) {
    if (event.type === 'habit.created' && event.habitId === habitId) {
      originalPlan = event.plan;
    } else if (
      event.type === 'habit.target_adjusted' &&
      event.habitId === habitId &&
      !reverted.has(event.eventId)
    ) {
      effectivePlan = {
        habitKind: event.adjustment.habitKind,
        schedule: event.adjustment.next,
      } as HabitPlan;
    }
  }

  return effectivePlan ?? originalPlan ?? fallback;
}

/**
 * The weekly planned number for a habit's CURRENT plan: a build habit's
 * target (daily counts as 7, one per day), or an avoid habit's weekly
 * allowance. Shared by every module that needs "what does this habit's
 * schedule ask for this week" (`../rewards/xp.ts`'s weekly-target-bonus scan,
 * `../review/weekly-review.ts`'s per-habit report, `../realism/
 * habit-realism.ts`'s suggestion baseline, `./projection.ts`'s
 * `DueHabitView.weekProgress`) -- previously four independent copies of the
 * same `kind === 'daily' ? 7 : targetPerWeek` / `limitPerWeek` ternary.
 */
export function weeklyPlannedFor(plan: HabitPlan): number {
  if (plan.habitKind === 'build') {
    return plan.schedule.kind === 'daily' ? 7 : plan.schedule.targetPerWeek;
  }
  return plan.schedule.limitPerWeek;
}

/**
 * Whether `actual` meets a habit's weekly plan: a build habit meets it at or
 * above `planned` (did enough); an avoid habit meets it at or below `planned`
 * (stayed under the allowance). Shared by the same callers as
 * `weeklyPlannedFor` above, for the identical reason.
 */
export function weeklyPlanMet(
  habitKind: 'build' | 'avoid',
  actual: number,
  planned: number,
): boolean {
  return habitKind === 'build' ? actual >= planned : actual <= planned;
}

/** Convenience re-export for callers that only need the adjustment's shape check. */
export type { TargetAdjustment };
